import React from 'react';
import styled from 'styled-components';
import WaveDivider from './WaveDivider.jsx';

const ProjectsSection = styled.section`
  background: white;
  padding: 0 0 100px 0;
  position: relative;
  overflow: hidden;
  isolation: isolate;
  width: 100%;
`;

const ProjectsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem 150px 2rem;
  
  @media (max-width: 768px) {
    padding: 0 1rem 100px 1rem;
  }
`;

const ExperienceHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  padding: 2rem;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.9) 0%, rgba(118, 75, 162, 0.9) 100%), 
              url('/images/backgrounds/synyx_01.jpg');
  background-size: cover;
  background-position: center;
  background-blend-mode: multiply;
  border-radius: 12px;
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.1);
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
    margin-bottom: 2rem;
  }
`;

const ExperienceCompany = styled.div`
  position: relative;
  z-index: 2;
`;

const CompanyLink = styled.a`
  text-decoration: none;
  color: inherit;
  transition: all 0.3s ease;
  display: inline-block;

  &:hover {
    transform: translateY(-2px);
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }

  h3 {
    font-size: 1.8rem;
    margin-bottom: 0.5rem;
    color: white;
    transition: all 0.3s ease;

    @media (max-width: 768px) {
      font-size: 1.4rem;
    }
  }
`;

const ExperienceRole = styled.p`
  font-size: 1.2rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: rgba(255, 255, 255, 0.9);

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ExperiencePeriod = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled.div`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(71, 85, 105, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border-top: 4px solid #64748b;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 40px rgba(71, 85, 105, 0.15);
  }
`;

const ProjectImage = styled.div`
  background: linear-gradient(135deg, #64748b, #475569);
  padding: 2rem;
  text-align: center;
  color: white;

  i {
    font-size: 3rem;
    color: #e2e8f0;
    margin-bottom: 1rem;
  }
`;

const ProjectContent = styled.div`
  padding: 2rem;

  h3 {
    color: #475569;
    margin-bottom: 1rem;
    font-size: 1.4rem;
    font-weight: 700;
  }

  p {
    color: #334155;
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }
`;

const ProjectType = styled.p`
  color: #667eea;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ProjectTech = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const TechTag = styled.span`
  background: linear-gradient(135deg, #e2e8f0, #cbd5e1);
  color: #475569;
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
    background: linear-gradient(135deg, #cbd5e1, #94a3b8);
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  color: #333;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, #667eea, #764ba2);
    border-radius: 2px;
  }
`;

const Projects = () => {
  const projects = [
    {
      title: 'IMTIS - Container Transport Platform',
      description: 'Entwicklung und Weiterentwicklung der IMTIS-Plattform für Contargo - ein innovatives Tool für Container-Transport-Preisanfragen. Implementierung von Features wie PDF-Export, Terminplanung und mobile Optimierung.',
      icon: 'fas fa-shipping-fast',
      technologies: ['Java', 'Spring Boot', 'Vue.js', 'REST APIs', 'PDF Generation', 'Mobile First'],
      type: 'Berufsprojekt bei synyx für Contargo',
      link: 'https://imtis.contargo.net/'
    },
    {
      title: 'Vue.js Frontend-Anwendungen',
      description: 'Entwicklung moderner, responsive Web-Anwendungen mit Vue.js. Implementierung von dynamischen Benutzeroberflächen, State Management und Integration mit Backend-APIs.',
      icon: 'fab fa-vuejs',
      technologies: ['Vue.js', 'JavaScript', 'TypeScript', 'HTML5', 'CSS3', 'REST Integration'],
      type: 'Frontend-Entwicklung'
    },
    {
      title: 'CI/CD Pipeline Automatisierung',
      description: 'Aufbau und Pflege von CI/CD-Pipelines für automatisierte Deployments. Implementierung von Build-, Test- und Deployment-Prozessen in Docker- und Kubernetes-Umgebungen.',
      icon: 'fas fa-cogs',
      technologies: ['Docker', 'Kubernetes', 'Helm', 'CI/CD', 'DevOps', 'Automation'],
      type: 'DevOps & Deployment'
    },
    {
      title: 'Datenbank-Design & SQL-Optimierung',
      description: 'Konzeption und Implementierung von Datenbankstrukturen mit MariaDB. Verwendung von Hibernate/JPA für Object-Relational Mapping und Optimierung von SQL-Queries für bessere Performance.',
      icon: 'fas fa-database',
      technologies: ['SQL', 'MariaDB', 'Hibernate', 'JPA', 'Datenmodellierung', 'Performance'],
      type: 'Backend & Datenbank'
    },
    {
      title: 'Automatisierte Test-Implementierung',
      description: 'Entwicklung umfassender Test-Strategien mit Unit-, Integrations- und End-to-End-Tests. Sicherstellung der Codequalität durch automatisierte Testing-Frameworks.',
      icon: 'fas fa-check-circle',
      technologies: ['JUnit5', 'Cypress', 'Unit Tests', 'Integration Tests', 'E2E Tests', 'Test Automation'],
      type: 'Quality Assurance'
    },
    {
      title: 'Monitoring & Observability',
      description: 'Implementierung von System-Monitoring und Observability-Lösungen. Überwachung von Anwendungsperformance, Metriken-Sammlung und Alert-Management für produktive Systeme.',
      icon: 'fas fa-chart-line',
      technologies: ['Prometheus', 'Grafana', 'Monitoring', 'Metrics', 'Observability', 'Alerting'],
      type: 'Operations & Monitoring'
    }
  ];

  return (
    <ProjectsSection id="projects" className="section">
      <SectionTitle>Berufserfahrung & Projekte</SectionTitle>
      <ExperienceHeader>
        <ExperienceCompany>
          <CompanyLink 
            href="https://synyx.de/" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <h3>synyx GmbH & Co. KG</h3>
          </CompanyLink>
          <ExperienceRole>Full-Stack Developerin</ExperienceRole>
          <ExperiencePeriod>Oktober 2022 - present</ExperiencePeriod>
        </ExperienceCompany>
      </ExperienceHeader>
      <ProjectsGrid>
        {projects.map((project, index) => (
          <ProjectCard key={index}>
            <ProjectImage>
              <i className={project.icon}></i>
            </ProjectImage>
            <ProjectContent>
              <h3>{project.title}</h3>
              <ProjectType>{project.type}</ProjectType>
              <p>{project.description}</p>
              <ProjectTech>
                {project.technologies.map((tech, techIndex) => (
                  <TechTag key={techIndex}>{tech}</TechTag>
                ))}
              </ProjectTech>
              {project.link && (
                <div style={{ marginTop: '1rem' }}>
                  <a 
                    href={project.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: '#667eea',
                      textDecoration: 'none',
                      fontWeight: '600',
                      padding: '0.5rem 1rem',
                      border: '2px solid #667eea',
                      borderRadius: '25px',
                      transition: 'all 0.3s ease',
                      fontSize: '0.9rem'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = '#667eea';
                      e.target.style.color = 'white';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = 'transparent';
                      e.target.style.color = '#667eea';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    <i className="fas fa-external-link-alt"></i>
                    Live Demo
                  </a>
                </div>
              )}
            </ProjectContent>
          </ProjectCard>
        ))}
      </ProjectsGrid>
      <WaveDivider 
        color="blue" 
        variant="dramatic" 
        animated={false}
        height={120}
      />
    </ProjectsSection>
  );
};

export default Projects;